import { createSlice } from '@reduxjs/toolkit';
import { mvdApi } from '../api/mvdApi';

const initialState = {
  mvds: [],
  selectedMVD: null,
  packagingSubcategories: [],
  selectedPackagingSubcategory: null,
  eprSubcategoryFees: [],
  selectedEPRSubcategoryFee: null,
  mvdStatuses: [],
  mvdTransitions: [],
  loading: false,
  error: null,
};

const mvdSlice = createSlice({
  name: 'mvd',
  initialState,
  reducers: {
    setSelectedMVD: (state, action) => {
      state.selectedMVD = action.payload;
    },
    clearSelectedMVD: (state) => {
      state.selectedMVD = null;
    },
    setSelectedPackagingSubcategory: (state, action) => {
      state.selectedPackagingSubcategory = action.payload;
    },
    clearSelectedPackagingSubcategory: (state) => {
      state.selectedPackagingSubcategory = null;
    },
    setSelectedEPRSubcategoryFee: (state, action) => {
      state.selectedEPRSubcategoryFee = action.payload;
    },
    clearSelectedEPRSubcategoryFee: (state) => {
      state.selectedEPRSubcategoryFee = null;
    },
    // Add action to directly set mock data if needed
    setMockData: (state, action) => {
      const { dataType, data } = action.payload;
      if (dataType === 'mvdStatuses') state.mvdStatuses = data;
      if (dataType === 'mvdTransitions') state.mvdTransitions = data;
      if (dataType === 'packagingSubcategories') state.packagingSubcategories = data;
      if (dataType === 'eprSubcategoryFees') state.eprSubcategoryFees = data;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle getMVDs
      .addMatcher(
        mvdApi.endpoints.getMVDs.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        mvdApi.endpoints.getMVDs.matchFulfilled,
        (state, action) => {
          state.loading = false;
          state.mvds = action.payload?.data?.data || [];
        }
      )
      .addMatcher(
        mvdApi.endpoints.getMVDs.matchRejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        }
      )
      
      // Handle getMVDById
      .addMatcher(
        mvdApi.endpoints.getMVDById.matchFulfilled,
        (state, action) => {
          state.selectedMVD = action.payload;
        }
      )
      
      // Handle getPackagingSubcategories with improved error handling
      .addMatcher(
        mvdApi.endpoints.getPackagingSubcategories.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        mvdApi.endpoints.getPackagingSubcategories.matchFulfilled,
        (state, action) => {
          state.loading = false;
          state.packagingSubcategories = action.payload?.data || [];
        }
      )
      .addMatcher(
        mvdApi.endpoints.getPackagingSubcategories.matchRejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        }
      )
      
      // Handle getEPRSubcategoryFees with improved error handling
      .addMatcher(
        mvdApi.endpoints.getEPRSubcategoryFees.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        mvdApi.endpoints.getEPRSubcategoryFees.matchFulfilled,
        (state, action) => {
          state.loading = false;
          state.eprSubcategoryFees = action.payload?.data || [];
        }
      )
      .addMatcher(
        mvdApi.endpoints.getEPRSubcategoryFees.matchRejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        }
      )
      
      // Handle getSubcategoryFees (which now returns mock data)
      .addMatcher(
        mvdApi.endpoints.getSubcategoryFees.matchFulfilled,
        (state, action) => {
          state.eprSubcategoryFees = action.payload?.data || [];
        }
      )
      
      // Handle getMVDStatuses with improved error handling
      .addMatcher(
        mvdApi.endpoints.getMVDStatuses.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        mvdApi.endpoints.getMVDStatuses.matchFulfilled,
        (state, action) => {
          state.loading = false;
          state.mvdStatuses = action.payload?.data || [];
        }
      )
      .addMatcher(
        mvdApi.endpoints.getMVDStatuses.matchRejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        }
      )
      
      // Handle getMVDTransitions with improved error handling
      .addMatcher(
        mvdApi.endpoints.getMVDTransitions.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        mvdApi.endpoints.getMVDTransitions.matchFulfilled,
        (state, action) => {
          state.loading = false;
          state.mvdTransitions = action.payload?.data || [];
        }
      )
      .addMatcher(
        mvdApi.endpoints.getMVDTransitions.matchRejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        }
      )
      
      // Handle getWasteStreams
      .addMatcher(
        mvdApi.endpoints.getWasteStreams.matchFulfilled,
        (state, action) => {
          // We don't store waste streams in state currently, but could add if needed
        }
      );
  },
});

export const { 
  setSelectedMVD, 
  clearSelectedMVD,
  setSelectedPackagingSubcategory,
  clearSelectedPackagingSubcategory,
  setSelectedEPRSubcategoryFee,
  clearSelectedEPRSubcategoryFee,
  setMockData
} = mvdSlice.actions;

export default mvdSlice.reducer;